import * as React from "react";
import { Link, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Toaster } from "@/components/ui/sonner";
import { AdminViewBanner } from "@/features/admin/AdminViewBanner";
import { AdminViewProvider } from "@/features/admin/AdminViewProvider";
import { useConsent } from "@/features/consent/ConsentProvider";

import { Sidebar } from "./Sidebar";
import { TopNav } from "./TopNav";
import { useDensity } from "./useDensity";

/**
 * AppLayout — Phase 6a.9 rebuild on shadcn/ui's SidebarProvider.
 *
 * Sidebar UX (design phase): the desktop sidebar defaults to the narrow
 * icon rail and expands on mouse hover (handled inside `<Sidebar>`), then
 * collapses again on mouse-leave. The `SidebarProvider` is therefore
 * CONTROLLED here with `open` starting `false`. The desktop hamburger is
 * gone; a `<SidebarTrigger>` is kept for mobile only (`md:hidden`).
 *
 * SPEC-08 changes retained:
 *   • `data-density` attribute on `<html>` via `useDensity`.
 *   • `<main>` horizontal padding `px-0` so `PageContainer` owns gutters.
 */
export function AppLayout() {
  useDensity();
  const { t } = useTranslation();
  const { openManager } = useConsent();
  // Controlled sidebar open-state. Starts collapsed (icon rail); hover
  // inside <Sidebar> flips it. Mobile uses the provider's own
  // openMobile/Sheet path, unaffected by this desktop flag.
  const [open, setOpen] = React.useState(false);
  return (
    <SidebarProvider open={open} onOpenChange={setOpen}>
      <AdminViewProvider>
        <Sidebar />
        <SidebarInset className="bg-background min-w-0">
          <TopNav leftSlot={<SidebarTrigger className="-ml-1 md:hidden" />} />
          {/* SPEC-23 — persistent warning banner while admin is operating
              on a borrowed engagement; hidden on `/` and `/admin`. */}
          <AdminViewBanner />
          <main className="flex-1 py-6 md:py-8 px-0">
            <Outlet />
          </main>
          {/* SPEC-COOKIE-2 — minimal footer with the cookie-policy link
              and a "Çerez tercihleri" trigger that opens the manage
              modal so users can change their mind from any logged-in
              screen. Anonymous routes (/login, /cerez-politikasi) get
              their own equivalent affordance. */}
          <footer className="border-t border-border px-4 py-3 text-xs text-muted-foreground">
            <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1">
              <Link
                to="/cerez-politikasi"
                className="hover:text-foreground hover:underline underline-offset-2"
                data-testid="cookie-policy-link"
              >
                {t("consent.policyLink")}
              </Link>
              <button
                type="button"
                onClick={openManager}
                className="hover:text-foreground hover:underline underline-offset-2"
                data-testid="cookie-prefs-link"
              >
                {t("consent.prefs")}
              </button>
            </div>
          </footer>
        </SidebarInset>
      </AdminViewProvider>
      {/* Phase 6a.17 — sonner toast host mounted once at the app shell. */}
      <Toaster richColors position="bottom-right" />
    </SidebarProvider>
  );
}
